import './lib/trustedTypes'
import { halasztottStilusBekotese } from './lib/deferredStyles'
import React, { lazy, Suspense, useEffect } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import App from './App'
import ConsentBanner from './components/ConsentBanner'
import { useRoute, type Route } from './lib/router'
import { DraftProvider } from './lib/draftStore'
import { track } from './lib/analytics'
import { useHozzajarulas } from './lib/consent'
import { pixelIndit, pixelLeallit, pixelOldalmegtekintes } from './lib/metaPixel'
import { kapcsolatiKattintasokFigyelese } from './lib/contactClicks'
import { rogzitBeerkezes } from './lib/attribution'
import { kliensMountMod } from './lib/clientMount'
import { installChunkLoadRecovery } from './lib/chunkLoadRecovery'
/*
  A 2.0 KASZKADJA: EGY RETEG, NEM HAROM.

  Korabban harom teljes atfestes allt egymason (`base` -> `redesign` ->
  `campaign`), es az importsorrend dontotte el, melyik nyer. Most minden
  lapnak egy dolga van: tokenek, alaprend, kozos keret, fooldal. Az
  aloldalak sajat lapjukat a sajat route-jukban toltik (lasd
  `performance.test.ts`).
*/
import './styles/tokens.css'
import './styles/base.css'
import './styles/chrome.css'
import './styles/home.css'
/* A hozzajarulasi sav a sajat nevtereben (`vx-consent`), minden oldalon. */
import './styles/consent.css'

installChunkLoadRecovery()

const QuotePage = lazy(() => import('./pages/QuotePage'))
const PaymentPage = lazy(() => import('./pages/PaymentPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const EstimatePage = lazy(() => import('./pages/EstimatePage'))
const OrderTrackPage = lazy(() => import('./pages/OrderTrackPage'))
const LegalPage = lazy(() => import('./pages/LegalPage'))

/**
 * Az oldalak utvonalankent kulon kotegben.
 *
 * ────────────────────────────────────────────────────────────────────
 * ELESITESI FELTETEL: SPA FALLBACK.
 *
 * A kiszolgalot ugy kell beallitani, hogy MINDEN ismeretlen utat az
 * `index.html`-re iranyitson, es az oldalt a domain GYOKEREBOL szolgalja
 * ki. Enelkul a `/arbecsles` es a `/ajanlat/VX-...` kozvetlen megnyitasa
 * (es a frissites) 404-et adna - utobbi pont az a link, amit e-mailben
 * kikuldunk.
 *
 * A `DraftProvider` a Root FOLOTT al. Ez SZANDEKOS: a fooldalon
 * kivalasztott `File` objektumoknak tul kell elniuk az utvonalvaltast, ami
 * a Root gyerekeit ujra mountolja.
 *
 * A ritkan hasznalt oldalak `lazy` importjai azt is biztositjak, hogy a
 * fooldal ne toltse le elore a 3D motort, az admin feluletet es az osszes
 * jogi dokumentum React-kodjat.
 * ────────────────────────────────────────────────────────────────────
 */
function Root(): JSX.Element {
  const route = useRoute()
  const { marketing } = useHozzajarulas()

  useEffect(() => {
    track('page_view', { route: route.name })
  }, [route.name])

  /*
    ────────────────────────────────────────────────────────────────
    A PIXEL A HOZZAJARULAST KOVETI - MINDKET IRANYBAN

    Nem csak az inditas fut le innen, hanem a LEALLITAS is. Egy
    hozzajarulaskezelo, ami csak elinditani tud, felkesz: a
    visszavonasnak (GDPR 7. cikk (3)) ugyanolyan azonnali hatasa kell
    hogy legyen, mint a megadasnak - kulonben az "Elutasitom" gomb
    csak a panelt tunteti el.

    A `pixelLeallit` a `_fbp` es `_fbc` sutit is torli, tehat a
    visszavonas utan nem marad ott az az azonosito, amit epp nem akar
    a latogato.
    ────────────────────────────────────────────────────────────────
  */
  useEffect(() => {
    if (marketing === 'megadva') pixelIndit()
    else pixelLeallit()
  }, [marketing])

  /*
    OLDALMEGTEKINTES UTVONALVALTASKOR.

    KULON hatas, es KULON fuggoseg (`route`, nem `route.name`). A sajat
    meres `page_view` esemenye szandekosan az utvonal NEVERE figyel - a
    tolcser abbol epul, es ez a szemantika nem valtozhat. A Meta
    feluleten viszont a `/ajanlat/A` -> `/ajanlat/B` valtas ket kulon
    megtekintes; azt itt kapjuk el. A dupla szamolast a `metaPixel.ts`
    utvonal-osszehasonlitasa zarja ki.
  */
  useEffect(() => {
    pixelOldalmegtekintes()
  }, [route])

  /* Telefon- es e-mail-kattintasok - egy figyelo az egesz oldalra. */
  useEffect(() => kapcsolatiKattintasokFigyelese(), [])

  return (
    <>
      {/*
        A PANEL A FA ELEJEN AL.

        Igy billentyuzettel az elso Tab mar ide hoz, anelkul hogy barkinek
        elvennenk a fokuszat. Vizualisan a lap aljan ul (`position:
        fixed`), tehat a sorrend csak a billentyuzetes eleresre hat.
      */}
      <ConsentBanner />
      <Oldal route={route} />
    </>
  )
}

/** A tenyleges lap. Kulon fuggveny, hogy a Root korai visszatereseit a
 *  panel folott is meg lehessen tartani. */
function Oldal({ route }: { route: Route }): JSX.Element {
  if (route.name === 'home') return <App />

  return (
    <Suspense fallback={<main className="route-loading" aria-busy="true">Betöltés…</main>}>
      {route.name === 'quote' ? <QuotePage slug={route.slug} /> : null}
      {route.name === 'payment' ? <PaymentPage token={route.token} /> : null}
      {route.name === 'estimate' ? <EstimatePage /> : null}
      {route.name === 'track' ? <OrderTrackPage /> : null}
      {route.name === 'legal' ? <LegalPage document={route.document} /> : null}
      {route.name === 'admin' ? <AdminPage /> : null}
    </Suspense>
  )
}

/*
  ────────────────────────────────────────────────────────────────────
  A HIRDETÉS-EREDET RÖGZÍTÉSE — A MOUNT ELŐTT, SZINKRONBAN

  Ez a hívás SZÁNDÉKOSAN nem `useEffect`-ben van. A `?fbadid=...` és a
  kampánycímkék csak a beérkezés pillanatában vannak a címsorban; mire
  a React lefut és az útválasztó dolgozni kezd, a query string már
  eltűnhet. Itt még biztosan megvan.

  A hívás nem dob és nem lassít: egyetlen `localStorage.setItem`, és
  csak akkor, ha van mit menteni.
*/
rogzitBeerkezes()
halasztottStilusBekotese()

const root = document.getElementById('root')!
const app = (
  <React.StrictMode>
    <DraftProvider>
      <Root />
    </DraftProvider>
  </React.StrictMode>
)

const mountMod = kliensMountMod(root, location.pathname)

if (mountMod === 'hydrate') {
  /*
    Az előrenderelt DOM már az első festéshez rendelkezésre áll. A külső rAF
    átengedi ezt a festést, és csak utána kapcsolja rá az eseménykezelőket;
    így a hosszú oldal hidratálása nem kerül az LCP előtti főszál-feladatba.

    A `kliensMountMod` garantálja, hogy ide csak a FŐOLDAL előrenderelt fája
    kerülhet. Egy SPA fallbackből érkező `/admin` vagy `/ajanlat/...` gyökere
    előbb kiürül, ezért az aloldal nem tapadhat a főoldal aljára.
  */
  requestAnimationFrame(() => hydrateRoot(root, app))
} else {
  createRoot(root).render(app)
}
